import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Scissors, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/AuthProvider";
import { useLatestBeautyScan } from "./useLatestBeautyScan";
import { ScanRequiredEmpty, ShopRecommendedButton } from "./beautyUi";
import StylePreviewButton from "./StylePreviewButton";

const LENGTHS = [
  { id: "auto",   label: "Auto" },
  { id: "short",  label: "Short" },
  { id: "medium", label: "Medium" },
  { id: "long",   label: "Long" },
];

export default function BeautyHairBeard() {
  const { user } = useAuth();
  const { scan, loading } = useLatestBeautyScan();
  const [gender, setGender] = useState(user?.gender || "");
  const [length, setLength] = useState("auto");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/beauty/hair/styles", {
        gender: gender || undefined,
        length_pref: length === "auto" ? undefined : length,
        scan_id: scan?.id,
      });
      setResult(data.result);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't fetch styles.";
      toast.error(String(msg));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!scan) return <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10"><ScanRequiredEmpty /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-hair-page">
      <p className="eyebrow flex items-center gap-2"><Scissors size={12} /> Stylebay · Hair &amp; Beard</p>
      <h1 className="editorial-h1 mt-3">Styles that <em>suit your face.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Curated hair, beard and eyebrow directions from your AI Scan — no product noise, just what looks best.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr,1.4fr] gap-10">
        <section className="border border-border p-6 lg:p-8 space-y-6 self-start" data-testid="beauty-hair-form">
          <div>
            <p className="eyebrow mb-3">Gender</p>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger data-testid="beauty-hair-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="nonbinary">Non-binary</SelectItem>
                <SelectItem value="prefer_not">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="eyebrow mb-3">Hair length preference</p>
            <div className="grid grid-cols-4 gap-2">
              {LENGTHS.map(l => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`beauty-hair-length-${l.id}`}
                  onClick={() => setLength(l.id)}
                  className={`h-10 text-[10px] uppercase tracking-[0.18em] border ${length === l.id ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"}`}
                >{l.label}</button>
              ))}
            </div>
          </div>
          <Button disabled={busy} onClick={run} className="w-full" data-testid="beauty-hair-generate">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Composing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Recommend styles</>}
          </Button>
        </section>

        <section data-testid="beauty-hair-result">
          {!result && !busy && (
            <div className="border border-dashed border-border p-10 text-center">
              <Scissors size={26} strokeWidth={1.3} className="mx-auto opacity-60" />
              <p className="font-serif text-xl mt-4">Recommendations appear here.</p>
              <p className="text-xs text-muted-foreground mt-2 max-w-sm mx-auto">
                Pick gender + length, tap Recommend. We&apos;ll match styles to your face shape and hair traits.
              </p>
            </div>
          )}
          {result && <HairOutput data={result} beardApplicable={scan?.analysis?.beard?.applicable} />}
        </section>
      </div>
    </div>
  );
}

function HairOutput({ data, beardApplicable }) {
  const { summary, hair_styles = [], beard_styles = [], eyebrow_shape } = data;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
      {summary && <p className="font-serif text-2xl italic leading-snug">&ldquo;{summary}&rdquo;</p>}

      {hair_styles.length > 0 && (
        <div>
          <p className="eyebrow mb-4">Hair styles</p>
          <ul className="grid sm:grid-cols-2 gap-4">
            {hair_styles.map((s, i) => (
              <li key={i} className="border border-border p-5 flex flex-col">
                <p className="font-serif text-xl">{s.name}</p>
                {s.description && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.description}</p>}
                {s.why_it_works && <p className="text-xs text-accent mt-3">Why it works · {s.why_it_works}</p>}
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  {s.length && <span>Length · {s.length}</span>}
                  {s.maintenance && <span>Upkeep · {s.maintenance}</span>}
                  {s.difficulty && <span>Difficulty · {s.difficulty}</span>}
                  {s.fade && s.fade !== "none" && <span>Fade · {s.fade}</span>}
                </div>
                {(s.color_ideas?.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.color_ideas.slice(0, 6).map((c, ci) => (
                      <span key={ci} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] border border-border px-2 py-1">
                        <span className="h-3 w-3" style={{ background: c.hex || "#ccc" }} />
                        {c.name}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <StylePreviewButton
                    kind="hair"
                    styleName={s.name}
                    styleNotes={[s.description, s.length && `length: ${s.length}`].filter(Boolean).join(". ")}
                    testId={`beauty-hair-preview-${i}`}
                  />
                  {s.shop_category && (
                    <ShopRecommendedButton category={s.shop_category} label="Shop products" testId={`beauty-hair-shop-${i}`} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {beardApplicable && beard_styles.length > 0 && (
        <div>
          <p className="eyebrow mb-4">Beard styles</p>
          <ul className="grid sm:grid-cols-2 gap-4">
            {beard_styles.map((s, i) => (
              <li key={i} className="border border-border p-5">
                <p className="font-serif text-xl">{s.name}</p>
                {s.description && <p className="text-sm text-muted-foreground mt-2">{s.description}</p>}
                {s.why_it_works && <p className="text-xs text-accent mt-3">Why it works · {s.why_it_works}</p>}
                {s.neckline_tip && <p className="text-xs text-muted-foreground mt-2">Neckline · {s.neckline_tip}</p>}
                {s.cheek_line_tip && <p className="text-xs text-muted-foreground">Cheek line · {s.cheek_line_tip}</p>}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <StylePreviewButton
                    kind="beard"
                    styleName={s.name}
                    styleNotes={s.description || ""}
                    testId={`beauty-beard-preview-${i}`}
                  />
                  {s.shop_category && (
                    <ShopRecommendedButton category={s.shop_category} label="Beard Care" testId={`beauty-beard-shop-${i}`} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {eyebrow_shape && (
        <div className="border border-border p-6">
          <p className="eyebrow mb-3">Eyebrow shape</p>
          <p className="font-serif text-xl">{eyebrow_shape.recommendation}</p>
          {eyebrow_shape.technique && <p className="text-sm text-muted-foreground mt-2">{eyebrow_shape.technique}</p>}
          {eyebrow_shape.tip && <p className="text-xs text-accent mt-2">Tip · {eyebrow_shape.tip}</p>}
        </div>
      )}
    </motion.div>
  );
}
